/**
 * A stable, anonymous id for THIS browser on THIS machine.
 *
 * Why it exists: the Login History page needs to tell "one person on one PC"
 * apart from "two people sharing a login". A User-Agent string cannot do that —
 * it changes every time Chrome updates, so one laptop looks like six devices.
 * This id is written once and never changes.
 *
 * What it is NOT: it is not a credential and it is not personal data. It grants
 * nothing, it is only ever a label next to a session, and the server treats it
 * as untrusted text.
 *
 * It must be impossible for this file to break the app, so:
 *   • every storage access is wrapped — Safari private mode, "block all
 *     cookies", a full disk quota and enterprise policies all throw here;
 *   • three levels of id generation, ending in one that needs no browser API;
 *   • if everything fails we still return a string, just a per-tab one.
 */

const STORAGE_KEY = "spearbi_device_id";

let cached = null;

const randomId = () => {
  try {
    if (typeof window !== "undefined" && window.crypto && typeof window.crypto.randomUUID === "function") {
      return "d_" + window.crypto.randomUUID().replace(/-/g, "");
    }
  } catch (e) {
    /* fall through */
  }
  try {
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return "d_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (e) {
    /* fall through */
  }
  // Last resort: no crypto API at all (very old browser, exotic webview).
  return "d_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

/**
 * Always returns a string. Never throws.
 */
export const getDeviceId = () => {
  if (cached) return cached;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && typeof stored === "string" && stored.length <= 64) {
      cached = stored;
      return cached;
    }
    const fresh = randomId();
    window.localStorage.setItem(STORAGE_KEY, fresh);
    cached = fresh;
    return cached;
  } catch (e) {
    // Storage unavailable or full: the id lives for this tab only. The page
    // simply shows that session as its own device, which is honest enough.
    cached = randomId();
    return cached;
  }
};

export default getDeviceId;
